/**
 * Lazy Singleton Utility
 *
 * Unlike the regular `singleton.ts` which instantiates immediately,
 * this version defers instantiation until the first property access.
 *
 * This is critical for code splitting: if a class imports heavy modules
 * (like TLDraw), using lazySingleton ensures those modules aren't loaded
 * until the singleton is actually used.
 *
 * Usage:
 *   export const myManager = lazySingleton<MyManager>(() => new MyManager());
 *
 *   // MyManager is NOT instantiated here
 *   import { myManager } from './myManager';
 *
 *   // MyManager IS instantiated on first property/method access
 *   myManager.init();
 */

class LazyHolder<Held> {
  private instance: Held | null = null;

  private builder: () => Held;

  constructor(builder: () => Held) {
    this.builder = builder;
  }

  getInstance(): Held {
    if (!this.instance) {
      this.instance = this.builder();
    }
    return this.instance;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazySingleton = <Shape extends Record<string, any>>(builder: () => Shape) => {
  const interceptor: ProxyHandler<LazyHolder<Shape>> = {
    get(holder, prop: string) {
      if (prop === '__debug') {
        return holder.getInstance();
      }

      const instance = holder.getInstance();
      const value = (instance as Record<string, unknown>)[prop];

      if (typeof value === 'function') {
        return (value as (...args: unknown[]) => unknown).bind(instance);
      }
      return value;
    },
    set(holder, prop: string, val: unknown) {
      const instance = holder.getInstance() as Record<string, unknown>;
      instance[prop] = val;
      return true;
    },
    ownKeys(holder) {
      return Object.keys(holder.getInstance());
    },
    has(holder, prop: string) {
      const instance = holder.getInstance();
      return prop in instance;
    },
    getOwnPropertyDescriptor(holder, prop: string) {
      const instance = holder.getInstance();
      return Object.getOwnPropertyDescriptor(instance, prop);
    },
  };

  return new Proxy(new LazyHolder(builder), interceptor) as unknown as Shape & { __debug: Shape };
};
